/**
 * Thin accessors for Capacitor.Preferences via the global native bridge.
 *
 * We intentionally don't `import("@capacitor/preferences")` here: Turbopack
 * in the app workspaces fails to load that package's lazy web-fallback chunk,
 * which leaves the destructured `Preferences` export undefined at runtime and
 * silently breaks every switch-platform / product-picker persist. The bridge
 * already mounts `window.Capacitor.Plugins.Preferences` so we use it directly.
 */

interface PreferencesBridge {
  get: (opts: { key: string }) => Promise<{ value: string | null }>;
  set: (opts: { key: string; value: string }) => Promise<void>;
  remove: (opts: { key: string }) => Promise<void>;
}

function getBridge(): PreferencesBridge | null {
  if (typeof window === "undefined") return null;
  const cap = (
    window as unknown as {
      Capacitor?: { Plugins?: { Preferences?: PreferencesBridge } };
    }
  ).Capacitor;
  return cap?.Plugins?.Preferences ?? null;
}

export async function setNativePreference(
  key: string,
  value: string,
): Promise<void> {
  const bridge = getBridge();
  if (!bridge) return;
  await bridge.set({ key, value });
}

export async function getNativePreference(key: string): Promise<string | null> {
  const bridge = getBridge();
  if (!bridge) return null;
  const result = await bridge.get({ key });
  return result.value;
}

export async function removeNativePreference(key: string): Promise<void> {
  const bridge = getBridge();
  if (!bridge) return;
  await bridge.remove({ key });
}
